import axios from 'axios'
import posthog from 'posthog-js'

import { hasPermission } from '../utils'
import { permissionLevels } from '../utils/variables'
import { shouldRefreshToken, isTokenExpired } from '../utils/tokenManager'
import {
  REFRESH_TOKEN_REQUEST,
  REFRESH_TOKEN_SUCCESS,
  REFRESH_TOKEN_FAILURE
} from '../actions/userActions'
import entorno from '../config/entorno'

const BASE_URL = entorno.baseApi
const BASE_DOMAIN = entorno.baseDominio
/**
 * ⚠️ Servidor local de desarrollo, fijo en el código.
 *
 * Lo usa quien pase `localAPI: true`. Hoy sólo lo hace
 * `purseReportsPaymentList` (`endpoint: 'reports/'`), así que **la pantalla de
 * reportes de pago de monedero apunta a localhost en todos los entornos** —
 * listado, creación y subida de Excel incluidos.
 *
 * No es una regresión de la migración: es trabajo contra un servidor local que
 * se quedó commiteado. Se detectó en la Fase 8 al centralizar las variables de
 * entorno, porque fue la única URL que no salía de ninguna.
 *
 * No se corrige aquí porque **no se sabe cuál es la URL real**: no hay ningún
 * `reports/` declarado en el resto del proyecto, y el comentario que había al
 * lado apuntaba a `credit_notes/`, que es otra cosa. Es una decisión de
 * producto, no de migración.
 *
 * Lo que sí se hace es que deje de fallar en silencio.
 */
const BASE_LOCAL_URL = 'http://localhost:3004/'
const BASE_URL_STORE = entorno.tienda

let isRefreshing = false
let failedQueue = []

/**
 * `msg` de las peticiones que admiten cancelación.
 *
 * Son listados con filtros: al cambiar un filtro se dispara una petición nueva
 * mientras la anterior sigue en vuelo, y si la vieja responde después, su
 * resultado pisa al de la nueva. Cancelarla evita esa carrera.
 *
 * NOTA: los actions de couriers también pasan `msg: 'courier'`
 * (courierActions.js:83 y :100), pero hoy esas peticiones nunca han sido
 * cancelables — la condición que lo habilitaba era código muerto. Habilitarlo
 * es un cambio de comportamiento aparte, no parte de esta corrección.
 */
const CANCELLABLE_MSG = new Set(['order'])

/**
 * Peticiones cancelables en vuelo, indexadas por `msg`.
 *
 * Indexar por `msg` es la corrección del bug B1: antes había una única variable
 * `cancel` global del módulo, y `if (cancel) cancel()` se ejecutaba en TODAS
 * las acciones interceptadas. Eso hacía que cualquier llamada posterior —de
 * couriers, del dashboard, o disparada por un WebSocket— abortara la petición
 * de órdenes en curso, dejando el listado vacío de forma intermitente.
 *
 * @type {Map<string, {token: object, cancel: Function}>}
 */
const pendingRequests = new Map()

/** Cancela la petición en vuelo de ese `msg`, si la hay. */
const cancelPendingRequest = msg => {
  const pending = pendingRequests.get(msg)
  if (!pending) return

  pending.cancel()
  pendingRequests.delete(msg)
}

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

const calculateUrl = (isPublic, localAPI, endpoint, storeAPI) => {
  let url
  if (localAPI) {
    if (!entorno.esDesarrollo && !entorno.esPrueba) {
      console.error(
        `⚠️ Petición a un servidor LOCAL desde un entorno desplegado: ` +
          `${BASE_LOCAL_URL}${endpoint}\n` +
          'Esa URL sólo existe en la máquina de quien desarrolla, así que ' +
          'esta petición va a fallar. La acción que la origina lleva ' +
          '`localAPI: true` y debería apuntar a un endpoint real.'
      )
    }
    url = `${BASE_LOCAL_URL}${endpoint}`
  } else if (isPublic) {
    url = `${BASE_DOMAIN}${endpoint}`
  } else if (storeAPI) {
    url = `${BASE_URL_STORE}${endpoint}`
  } else {
    url = `${BASE_URL}${endpoint}`
  }
  return url
}

export function callApi(
  method,
  endpoint,
  data,
  authenticated,
  localAPI,
  isPublic,
  msg,
  storeAPI
) {
  const accessToken = localStorage.getItem('accessToken') || null
  const config = {
    method,
    url: calculateUrl(isPublic, localAPI, endpoint, storeAPI),
    data
  }

  let source = null
  if (CANCELLABLE_MSG.has(msg)) {
    source = axios.CancelToken.source()
    pendingRequests.set(msg, source)
    config.cancelToken = source.token
  }

  if (authenticated) {
    if (accessToken) {
      const headers = {
        Authorization: `JWT ${accessToken}`,
        'Accept-Language': 'es',
        'Time-Zone': Intl.DateTimeFormat().resolvedOptions().timeZone
      }
      const sessionId = posthog.get_session_id()
      if (sessionId) {
        headers['X-PostHog-Session-Id'] = sessionId
      }
      config.headers = headers
    } else {
      console.error('❌ No access token found - redirecting to login')
      window.location = '/login/'
      return Promise.reject(new Error('No access token'))
    }
  }

  if (!source) return axios(config)

  return axios(config).finally(() => {
    // Sólo se limpia si seguimos siendo la petición vigente. Si otra más nueva
    // ya nos reemplazó en el mapa, borrar aquí eliminaría SU entrada y la
    // dejaría sin poder cancelarse.
    if (pendingRequests.get(msg) === source) {
      pendingRequests.delete(msg)
    }
  })
}

export const CALL_API = Symbol('Call API')

export default store => next => action => {
  const callAPI = action[CALL_API]

  // So the middleware doesn't get applied to every single action
  if (typeof callAPI === 'undefined') {
    return next(action)
  }

  const {
    method,
    endpoint,
    data,
    authenticated,
    types,
    extra,
    localAPI,
    isPublic,
    msg,
    storeAPI
  } = callAPI
  const [requestType, successType, errorType] = types

  // Cancela sólo la petición anterior DEL MISMO `msg`. Antes esto era
  // `if (cancel) cancel()` sobre una variable global, así que cualquier acción
  // abortaba la petición de órdenes en vuelo (bug B1).
  if (msg) cancelPendingRequest(msg)

  next({ type: requestType, extra })

  if (!hasPermission('shop', permissionLevels.READ) && storeAPI) {
    return false
  }

  // Verificar si necesitamos refrescar el token antes de hacer la llamada
  if (
    authenticated &&
    shouldRefreshToken() &&
    !endpoint.includes('authenticate/jwt')
  ) {
    console.log('🔄 Token needs refresh before API call')

    if (!isRefreshing) {
      isRefreshing = true
      const storedRefresh = localStorage.getItem('refreshToken')
      store.dispatch({ type: REFRESH_TOKEN_REQUEST })

      return axios({
        method: 'POST',
        url: `${BASE_URL}authenticate/jwt/refresh/`,
        data: { refresh: storedRefresh }
      })
        .then(response => {
          console.log('✅ Token refreshed successfully')
          store.dispatch({ type: REFRESH_TOKEN_SUCCESS, data: response.data })
          isRefreshing = false
          processQueue(null, response.data.access)
          return callApi(
            method,
            endpoint,
            data,
            authenticated,
            localAPI,
            isPublic,
            msg,
            storeAPI
          )
            .then(callResponse => {
              next({ extra, data: callResponse.data, type: successType })
            })
            .catch(callError => {
              next({
                errors: callError.response
                  ? callError.response.data
                  : { error: 'error' },
                type: errorType,
                status: callError.response?.status
              })
            })
        })
        .catch(error => {
          const isAuthError =
            error.response?.status === 401 || error.response?.status === 400
          console.error(
            '❌ Pre-call refresh failed:',
            isAuthError
              ? `HTTP ${error.response.status}`
              : 'network/server error'
          )
          isRefreshing = false
          processQueue(error, null)
          if (isAuthError) {
            store.dispatch({
              type: REFRESH_TOKEN_FAILURE,
              errors: error.response?.data
            })
            window.location = '/login'
          } else {
            next({
              errors: {
                detail:
                  'Error de conexión al renovar sesión. Intente nuevamente.'
              },
              type: errorType,
              // Sin `status`: no hubo respuesta del servidor. Ésa es la señal
              // de que el fallo es de red y la sesión no debe cerrarse.
              status: undefined
            })
          }
        })
    }

    // Si ya se está refrescando, agregar a la cola
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject })
    })
      .then(() => {
        return callApi(
          method,
          endpoint,
          data,
          authenticated,
          localAPI,
          isPublic,
          msg,
          storeAPI
        )
      })
      .then(response => {
        next({
          extra,
          data: response.data,
          type: successType
        })
      })
      .catch(error => {
        // Ídem que en el manejador principal: cancelar no es fallar.
        if (axios.isCancel(error)) return

        return next({
          errors: error.response ? error.response.data : { error: 'error' },
          type: errorType,
          status: error.response?.status
        })
      })
  }

  // Llamada normal sin necesidad de refresh
  return callApi(
    method,
    endpoint,
    data,
    authenticated,
    localAPI,
    isPublic,
    msg,
    storeAPI
  )
    .then(response => {
      next({
        extra,
        data: response.data,
        type: successType
      })

      // Identify user in PostHog after successful login
      if (endpoint.includes('authenticate/jwt') && response.data.access) {
        try {
          const admin = response.data.admin
          const decoded = JSON.parse(atob(response.data.access.split('.')[1]))
          const identifier = admin?.id || decoded?.user_id

          if (identifier) {
            const properties = {
              role: 'admin'
            }

            if (admin && admin.user) {
              properties.email = admin.user.email
              properties.first_name = admin.user.first_name
              properties.last_name = admin.user.last_name
              properties.city = admin.cities?.[0]?.name
              properties.admin_groups = admin.groups?.map(g => g.name)
            }

            posthog.identify(identifier, properties)
          }
        } catch (e) {
          console.error('Error identifying user in PostHog:', e)
        }
      }
    })
    .catch(error => {
      /**
       * Una petición cancelada no es un fallo.
       *
       * `CANCELLABLE_MSG` hace que una petición nueva aborte la anterior del
       * mismo `msg` — es deliberado y es lo que evita que una respuesta vieja
       * pise a una reciente. Pero axios entrega esa cancelación por el mismo
       * `catch` que un error de red, y sin `error.response`, así que acababa:
       *
       *   1. registrada como `❌ API Error … {status: undefined}`,
       *   2. registrada otra vez como `❌ Network Error - No response`, y
       *   3. **despachada como FAILURE** con «verifique su conexión a
       *      internet».
       *
       * Lo tercero es lo que importa: hoy nadie recoge ese FAILURE en el
       * tablero, pero el día que alguien le ponga un aviso, el operador vería
       * errores de conexión inventados. Y lo primero llenaba la consola de
       * rojo, que fue como se encontró, validando `pre` el 2026-08-25.
       *
       * No se despacha nada: `cancelPendingRequest` corre inmediatamente antes
       * del `next({ type: requestType })` de la petición que reemplaza a ésta,
       * así que el ciclo lo cierra ella. Fallo preexistente.
       */
      if (axios.isCancel(error)) return

      // Manejo mejorado de errores
      const status = error.response?.status
      const errorData = error.response?.data

      console.error(`❌ API Error: ${method} ${endpoint}`, {
        status,
        data: errorData
      })

      // Error 401 - No autorizado o token inválido
      if (status === 401) {
        console.error('❌ 401 Unauthorized')

        // Si es el endpoint de login, NO hacer redirect (las credenciales son incorrectas)
        if (endpoint.includes('authenticate/jwt')) {
          console.error('❌ Login failed - Invalid credentials')
          return next({
            errors: errorData || {
              detail: 'Credenciales incorrectas'
            },
            type: errorType,
            status
          })
        }

        // Para otros endpoints: intentar refresh antes de hacer logout
        const storedRefreshToken = localStorage.getItem('refreshToken')
        if (!storedRefreshToken || isTokenExpired(storedRefreshToken)) {
          console.error('❌ No valid refresh token - logging out')
          localStorage.clear()
          window.location = '/login'
          return next({
            errors: {
              detail: 'Sesión expirada. Por favor, inicie sesión nuevamente.'
            },
            type: errorType,
            status
          })
        }

        console.log('🔄 Got 401 - attempting refresh and retry')

        if (!isRefreshing) {
          isRefreshing = true
          store.dispatch({ type: REFRESH_TOKEN_REQUEST })
          return axios({
            method: 'POST',
            url: `${BASE_URL}authenticate/jwt/refresh/`,
            data: { refresh: storedRefreshToken }
          })
            .then(response => {
              console.log('✅ Token refreshed after 401 - retrying request')
              store.dispatch({
                type: REFRESH_TOKEN_SUCCESS,
                data: response.data
              })
              isRefreshing = false
              processQueue(null, response.data.access)
              return callApi(
                method,
                endpoint,
                data,
                authenticated,
                localAPI,
                isPublic,
                msg,
                storeAPI
              )
                .then(retryResponse => {
                  next({ extra, data: retryResponse.data, type: successType })
                })
                .catch(retryError => {
                  next({
                    errors: retryError.response
                      ? retryError.response.data
                      : { error: 'error' },
                    type: errorType,
                    status
                  })
                })
            })
            .catch(refreshError => {
              const isAuthError =
                refreshError.response?.status === 401 ||
                refreshError.response?.status === 400
              console.error(
                '❌ Post-401 refresh failed:',
                isAuthError
                  ? `HTTP ${refreshError.response.status}`
                  : 'network/server error'
              )
              isRefreshing = false
              processQueue(refreshError, null)
              if (isAuthError) {
                store.dispatch({
                  type: REFRESH_TOKEN_FAILURE,
                  errors: refreshError.response?.data
                })
                window.location = '/login'
              }
            })
        }

        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(() =>
            callApi(
              method,
              endpoint,
              data,
              authenticated,
              localAPI,
              isPublic,
              msg,
              storeAPI
            )
          )
          .then(response =>
            next({ extra, data: response.data, type: successType })
          )
          .catch(err =>
            next({
              errors: err.response ? err.response.data : { error: 'error' },
              type: errorType,
              status
            })
          )
      }

      // Error 400 - Bad Request (validación de campos)
      if (status === 400) {
        console.error('❌ 400 Bad Request - Validation errors:', errorData)
        return next({
          errors: errorData || { error: 'Datos inválidos' },
          type: errorType,
          status
        })
      }

      // Error 403 - Forbidden
      if (status === 403) {
        console.error('❌ 403 Forbidden - Insufficient permissions')
        return next({
          errors: errorData || {
            detail: 'No tiene permisos para realizar esta acción'
          },
          type: errorType,
          status
        })
      }

      // Error 404 - Not Found
      if (status === 404) {
        console.error('❌ 404 Not Found')
        return next({
          errors: errorData || { detail: 'Recurso no encontrado' },
          type: errorType,
          status
        })
      }

      // Error 500 - Server Error
      if (status === 500) {
        console.error('❌ 500 Internal Server Error')
        return next({
          errors: errorData || {
            detail: 'Error del servidor. Intente nuevamente más tarde.'
          },
          type: errorType,
          status
        })
      }

      // Error de red o sin respuesta
      if (!error.response) {
        console.error('❌ Network Error - No response from server')
        return next({
          errors: {
            detail: 'Error de conexión. Verifique su conexión a internet.'
          },
          type: errorType,
          status
        })
      }

      // Otros errores
      return next({
        errors: errorData || { error: 'Error desconocido' },
        type: errorType,
        status
      })
    })
}
